import React from "react";

const PsychologyLeversVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
        This lesson shows how promotion signals compound when psychological levers are stacked, not used alone. It frames a funnel where not promoting you starts to feel riskier than promoting you: build familiarity, then trust, then ownership, then scarcity and loss aversion, and finish with social proof, authority, and clear credit. The idea is to pick a few levers that fit your current gap and layer them deliberately until promotion feels obvious.
      </p>
    </div>
  );
const transcript = (
  <div className="pt-2 pb-6 space-y-4 text-body leading-7 text-[#323232] overflow-y-scroll font-gotham">
    <p>
      The psychological levers we talked about aren’t meant to be used in
      isolation. They stack. And that’s where this gets powerful.
    </p>

    <p>
      Think of them as a funnel that makes NOT promoting you
      feel riskier than promoting you.
    </p>

    <p>
      <strong>LAYER 1: FAMILIARITY</strong> (Top of funnel)
    </p>

    <p>
      Start with mere exposure. Get your face, name, and work in front of
      decision-makers repeatedly. Build familiarity.
    </p>

    <p>
      <strong>LAYER 2: TRUST</strong> (Second layer)
    </p>

    <p>
      Then layer on trust. Use the halo effect (be known for one strong trait)
      and reciprocity (make their lives easier).
    </p>

    <p>
      Now they know you AND trust you.
    </p>

    <p>
      <strong>LAYER 3: OWNERSHIP</strong> (Third layer)
    </p>

    <p>
      Next, create ownership. Use endowment (let them co-shape your work) and
      commitment (get them to publicly commit to your promotion path).
    </p>

    <p>
      Now they’re personally invested in your success.
    </p>

    <p>
      <strong>LAYER 4: SCARCITY</strong> (Fourth layer)
    </p>

    <p>
      Then introduce scarcity and loss aversion. Remind them subtly that your
      skill mix is rare and you have options.
    </p>

    <p>
      At this point, NOT promoting you feels like a real risk.
    </p>

    <p>
      <strong>LAYER 5: SOCIAL PROOF</strong> (Bottom of funnel)
    </p>

    <p>
      Finally, add social proof (others publicly value you), authority
      signaling (you communicate like a leader), and clear attribution (your
      wins are unmistakably yours).
    </p>

    <p>
      By the time they reach the bottom of this funnel, the question isn’t
      “Should we promote them?”
    </p>


<p>
  It’s “How are they NOT already promoted?”
</p>

<p>
  <strong>
    Each layer makes not promoting you feel riskier than promoting you.
  </strong>
</p>

<p>
  That’s the power of stacking these levers strategically.
</p>

<p>
  You don’t need to use all ten levers tomorrow. Pick two or three that feel
  most natural to where you are right now.
</p>

<p>
  Maybe you need more visibility, lean into mere-exposure and social proof.
</p>

<p>
  Maybe you’re visible but not trusted, focus on reciprocity and the halo
  effect.
</p>

<p>
  Maybe you’re stuck despite doing good work, time for commitment, scarcity,
  and loss aversion.
</p>

<p>
  The point is promotions are about who works{" "}
  <em>smartest with human nature</em>. The person who understands the game has
  a better chance of winning it.
</p>
  </div>
);

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default PsychologyLeversVideo;
